import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Banner,
  Button,
  buttonVariants,
  Checkbox,
  FormError,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  sonnerToast,
  Textarea,
  useDocumentTitle,
} from "@simplicity/ui";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { apiFetch } from "../../api";
import type { ExportDefinitionDetail, Project } from "../../configTypes";
import {
  EXPORT_CADENCE_OPTIONS,
  EXPORT_DESTINATION_TYPE_OPTIONS,
  EXPORT_FORMAT_OPTIONS,
} from "../../configTypes";
import { useDetailForm } from "../../hooks/useDetailForm";
import { DESTRUCTIVE_BUTTON_CLASS, tryParseJson } from "../../utils";
import { Breadcrumbs } from "./Breadcrumbs";
import { SqlTextarea } from "./SqlTextarea";
import { useDirtyForm } from "./useDirtyForm";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Delete confirmation dialog. */
function DeleteDialog({
  open,
  onOpenChange,
  definitionName,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  definitionName: string;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete export definition?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete &ldquo;{definitionName}&rdquo;. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: "destructive" })}
            onClick={onConfirm}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Form-field state grouped together so the parent hook stays small. */
function useExportFormFields() {
  const [name, setName] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [cadence, setCadence] = useState<string>(EXPORT_CADENCE_OPTIONS[0].value);
  const [sqlTemplate, setSqlTemplate] = useState("");
  const [format, setFormat] = useState<string>(EXPORT_FORMAT_OPTIONS[0].value);
  const [destinationType, setDestinationType] = useState<string>(
    EXPORT_DESTINATION_TYPE_OPTIONS[0].value,
  );
  const [filenameTemplate, setFilenameTemplate] = useState("");
  const [secretRef, setSecretRef] = useState("");
  const [columnOrderText, setColumnOrderText] = useState("[]");
  const [paramsText, setParamsText] = useState("{}");
  const [destinationVarsText, setDestinationVarsText] = useState("{}");

  const applyFromServer = useCallback((ed: ExportDefinitionDetail) => {
    setName(ed.name);
    setEnabled(ed.enabled);
    setCadence(ed.cadence);
    setSqlTemplate(ed.sqlTemplate);
    setFormat(ed.format);
    setDestinationType(ed.destinationType);
    setFilenameTemplate(ed.filenameTemplate);
    setSecretRef(ed.secretRef);
    setColumnOrderText(JSON.stringify(ed.columnOrder, null, 2));
    setParamsText(JSON.stringify(ed.params, null, 2));
    setDestinationVarsText(JSON.stringify(ed.destinationVars, null, 2));
  }, []);

  return {
    name,
    setName,
    enabled,
    setEnabled,
    cadence,
    setCadence,
    sqlTemplate,
    setSqlTemplate,
    format,
    setFormat,
    destinationType,
    setDestinationType,
    filenameTemplate,
    setFilenameTemplate,
    secretRef,
    setSecretRef,
    columnOrderText,
    setColumnOrderText,
    paramsText,
    setParamsText,
    destinationVarsText,
    setDestinationVarsText,
    applyFromServer,
  };
}

/** Serialize + validate the PATCH body from the current form fields. */
function buildUpdateBody(
  fields: ReturnType<typeof useExportFormFields>,
): { ok: true; body: string } | { ok: false; error: string } {
  const columnOrderResult = tryParseJson(fields.columnOrderText);
  if (!columnOrderResult.ok || !Array.isArray(columnOrderResult.value)) {
    return { ok: false, error: "Column order must be a JSON array of column names." };
  }
  const paramsResult = tryParseJson(fields.paramsText);
  if (!paramsResult.ok) return { ok: false, error: `Params: ${paramsResult.error}` };
  const destinationVarsResult = tryParseJson(fields.destinationVarsText);
  if (!destinationVarsResult.ok) {
    return { ok: false, error: `Destination vars: ${destinationVarsResult.error}` };
  }
  return {
    ok: true,
    body: JSON.stringify({
      name: fields.name.trim(),
      enabled: fields.enabled,
      cadence: fields.cadence,
      sqlTemplate: fields.sqlTemplate,
      params: paramsResult.value,
      format: fields.format,
      filenameTemplate: fields.filenameTemplate.trim(),
      columnOrder: columnOrderResult.value,
      destinationType: fields.destinationType,
      destinationVars: destinationVarsResult.value,
      secretRef: fields.secretRef.trim(),
    }),
  };
}

/** Custom hook encapsulating export definition fetch, save, and delete logic. */
function useExportDefinition(id: string | undefined) {
  const navigate = useNavigate();
  const fields = useExportFormFields();

  const [definition, setDefinition] = useState<ExportDefinitionDetail | null>(null);
  const [projectName, setProjectName] = useState("");

  const { isDirty, setBaseline, resetBaseline } = useDirtyForm({
    name: fields.name,
    enabled: String(fields.enabled),
    cadence: fields.cadence,
    sqlTemplate: fields.sqlTemplate,
    format: fields.format,
    destinationType: fields.destinationType,
    filenameTemplate: fields.filenameTemplate,
    secretRef: fields.secretRef,
    columnOrderText: fields.columnOrderText,
    paramsText: fields.paramsText,
    destinationVarsText: fields.destinationVarsText,
  });

  function baselineFrom(ed: ExportDefinitionDetail) {
    return {
      name: ed.name,
      enabled: String(ed.enabled),
      cadence: ed.cadence,
      sqlTemplate: ed.sqlTemplate,
      format: ed.format,
      destinationType: ed.destinationType,
      filenameTemplate: ed.filenameTemplate,
      secretRef: ed.secretRef,
      columnOrderText: JSON.stringify(ed.columnOrder, null, 2),
      paramsText: JSON.stringify(ed.params, null, 2),
      destinationVarsText: JSON.stringify(ed.destinationVars, null, 2),
    };
  }

  const form = useDetailForm<ExportDefinitionDetail>({
    id,
    fetcher: (eid) => apiFetch<ExportDefinitionDetail>(`/api/export-definitions/${eid}`),
    onLoaded: async (ed) => {
      setDefinition(ed);
      fields.applyFromServer(ed);
      setBaseline(baselineFrom(ed));
      const project = await apiFetch<Project>(`/api/projects/${ed.projectId}`);
      setProjectName(project.name);
    },
    validate: () => {
      const built = buildUpdateBody(fields);
      return built.ok ? null : built.error;
    },
    saver: () => {
      const built = buildUpdateBody(fields);
      // `validate` runs first and short-circuits on error, so this is safe.
      const body = built.ok ? built.body : "{}";
      return apiFetch<ExportDefinitionDetail>(`/api/export-definitions/${id}`, {
        method: "PATCH",
        body,
      });
    },
    onSaved: (updated) => {
      setDefinition(updated);
      fields.applyFromServer(updated);
      resetBaseline();
    },
    successMessage: "Export definition saved.",
  });

  async function handleDelete() {
    try {
      await apiFetch(`/api/export-definitions/${id}`, { method: "DELETE" });
      sonnerToast.info("Export definition deleted");
      void navigate(
        definition ? `/config/projects/${definition.projectId}#exports` : "/config/projects",
      );
    } catch (err) {
      sonnerToast.error(err instanceof Error ? err.message : "Delete failed");
    }
  }

  return {
    ...fields,
    definition,
    projectName,
    loading: form.loading,
    error: form.error,
    saving: form.saving,
    saveLabel: form.saveLabel,
    notification: form.notification,
    clearBanner: form.clearNotification,
    isDirty,
    save: form.save,
    handleDelete,
  };
}

/** Identity + delivery fields. */
type Fields = ReturnType<typeof useExportDefinition>;

/** Identity row: name, cadence, enabled. */
function IdentityBlock({ ed, clear }: { ed: Fields; clear: () => void }) {
  return (
    <div className="mb-4 grid grid-cols-[1fr_160px_auto] gap-4">
      <div>
        <Label htmlFor="ed-name" className="form-label">
          Name
        </Label>
        <Input
          id="ed-name"
          value={ed.name}
          onChange={(e) => {
            ed.setName(e.target.value);
            clear();
          }}
          placeholder="e.g. final_forms"
        />
      </div>
      <div>
        <Label htmlFor="ed-cadence" className="form-label">
          Cadence
        </Label>
        <Select
          value={ed.cadence}
          onValueChange={(v) => {
            ed.setCadence(v);
            clear();
          }}
        >
          <SelectTrigger id="ed-cadence" data-testid="ed-cadence-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EXPORT_CADENCE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-end pb-1">
        <Label className="flex items-center gap-2 text-sm text-foreground">
          <Checkbox
            checked={ed.enabled}
            onCheckedChange={(v) => {
              ed.setEnabled(v === true);
              clear();
            }}
          />
          Enabled
        </Label>
      </div>
    </div>
  );
}

/** Delivery rows: format + destination type, then filename + secret ref. */
function DeliveryBlock({ ed, clear }: { ed: Fields; clear: () => void }) {
  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ed-format" className="form-label">
            Format
          </Label>
          <Select
            value={ed.format}
            onValueChange={(v) => {
              ed.setFormat(v);
              clear();
            }}
          >
            <SelectTrigger id="ed-format">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPORT_FORMAT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="ed-destination-type" className="form-label">
            Destination type
          </Label>
          <Select
            value={ed.destinationType}
            onValueChange={(v) => {
              ed.setDestinationType(v);
              clear();
            }}
          >
            <SelectTrigger id="ed-destination-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPORT_DESTINATION_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ed-filename" className="form-label">
            Filename template
          </Label>
          <Input
            id="ed-filename"
            value={ed.filenameTemplate}
            onChange={(e) => {
              ed.setFilenameTemplate(e.target.value);
              clear();
            }}
            placeholder="e.g. final_forms_{date}.csv"
          />
        </div>
        <div>
          <Label htmlFor="ed-secret-ref" className="form-label">
            Secret ref
          </Label>
          <Input
            id="ed-secret-ref"
            value={ed.secretRef}
            onChange={(e) => {
              ed.setSecretRef(e.target.value);
              clear();
            }}
            placeholder="e.g. final-forms-sftp"
          />
        </div>
      </div>
    </>
  );
}

/** SQL editor + the three JSON config textareas. */
function ConfigBlock({ ed, clear }: { ed: Fields; clear: () => void }) {
  return (
    <>
      <div className="mb-4">
        <Label htmlFor="ed-sql" className="form-label">
          SQL Template
        </Label>
        <SqlTextarea
          id="ed-sql"
          value={ed.sqlTemplate}
          onChange={(v) => {
            ed.setSqlTemplate(v);
            clear();
          }}
        />
      </div>

      <div className="mb-4">
        <Label htmlFor="ed-column-order" className="form-label">
          Column order (JSON array)
        </Label>
        <Textarea
          id="ed-column-order"
          className="min-h-[80px] font-mono"
          value={ed.columnOrderText}
          onChange={(e) => {
            ed.setColumnOrderText(e.target.value);
            clear();
          }}
          placeholder='["student_id", "name"]'
        />
      </div>

      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ed-params" className="form-label">
            Params (JSON)
          </Label>
          <Textarea
            id="ed-params"
            className="min-h-[120px] font-mono"
            value={ed.paramsText}
            onChange={(e) => {
              ed.setParamsText(e.target.value);
              clear();
            }}
            placeholder="{}"
          />
        </div>
        <div>
          <Label htmlFor="ed-destination-vars" className="form-label">
            Destination vars (JSON)
          </Label>
          <Textarea
            id="ed-destination-vars"
            className="min-h-[120px] font-mono"
            value={ed.destinationVarsText}
            onChange={(e) => {
              ed.setDestinationVarsText(e.target.value);
              clear();
            }}
            placeholder="{}"
          />
        </div>
      </div>
    </>
  );
}

/** Identity + delivery + config fields. */
function ExportFields({ ed }: { ed: Fields }) {
  const clear = ed.clearBanner;
  return (
    <>
      <IdentityBlock ed={ed} clear={clear} />
      <DeliveryBlock ed={ed} clear={clear} />
      <ConfigBlock ed={ed} clear={clear} />
    </>
  );
}

/** Edit form body, given the loaded definition + form-state object. */
function ExportEditForm({
  definition,
  ed,
  onDeleteOpen,
}: {
  definition: ExportDefinitionDetail;
  ed: ReturnType<typeof useExportDefinition>;
  onDeleteOpen: () => void;
}) {
  return (
    <div className="form-card mb-6" data-testid="ed-page-wrapper">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-lg text-foreground">Edit Export</h2>
        <span className="truncate font-mono text-xs text-muted-foreground" title={definition.id}>
          ID: {definition.id}
        </span>
      </div>
      {ed.notification && (
        <Banner variant={ed.notification.variant} onDismiss={ed.clearBanner} className="mb-4">
          {ed.notification.message}
        </Banner>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void ed.save();
        }}
      >
        <ExportFields ed={ed} />

        <div className="mb-6 flex gap-6 text-xs text-muted-foreground">
          <span>Created: {formatDate(definition.createdAt)}</span>
          <span>Updated: {formatDate(definition.updatedAt)}</span>
        </div>

        {!ed.name.trim() && <FormError>Name is required.</FormError>}

        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            className={DESTRUCTIVE_BUTTON_CLASS}
            onClick={onDeleteOpen}
          >
            Delete
          </Button>
          <Button
            type="submit"
            disabled={ed.saving || !ed.isDirty || !ed.name.trim() || !ed.filenameTemplate.trim()}
          >
            {ed.saving ? "Saving..." : ed.saveLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function ExportDefinitionDetails() {
  const { id } = useParams<{ id: string }>();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const ed = useExportDefinition(id);
  const { dialog: unsavedChangesDialog } = useUnsavedChangesGuard(ed.isDirty);

  useDocumentTitle(ed.definition?.name ?? null, { suffix: " | Simplicity Admin" });

  if (ed.loading) return <div className="empty-state">Loading export definition...</div>;
  if (ed.error || !ed.definition) {
    return (
      <div className="empty-state text-destructive">
        {ed.error ?? "Export definition not found"}
      </div>
    );
  }

  const { definition } = ed;

  return (
    <div className="mx-auto w-full max-w-4xl">
      <Breadcrumbs
        items={[
          { label: "Projects", to: "/config/projects" },
          {
            label: ed.projectName || "...",
            to: `/config/projects/${definition.projectId}#exports`,
          },
          { label: definition.name },
        ]}
      />
      <ExportEditForm definition={definition} ed={ed} onDeleteOpen={() => setDeleteOpen(true)} />
      <DeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        definitionName={definition.name}
        onConfirm={() => void ed.handleDelete()}
      />
      {unsavedChangesDialog}
    </div>
  );
}
